import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, RefreshCw, CheckCircle, AlertCircle, Shield, Smartphone } from "lucide-react";
import parentStyles from "../CreatorApplication.module.css";
import Logo from "../../../../assets/Logo.svg";
import { showToast } from "../../../../utils/toast";
import {
  createDiditSessionAPI,
  getDiditVerificationByUserIdAPI,
  deleteVerificationAPI,
} from "../../../../api/diditApi";
import { isStagingCreatorOnboardingBypassEnabled } from "../../../../config/stagingCreatorOnboarding";
import {
  normalizeDiditStatus,
  isDiditSuccessStatus,
  isDiditFailureStatus,
  registerDiditVerificationAttempt,
  trackDiditVerificationFailureOutcome,
  getDiditVerificationAttemptCount,
} from "../../../../analytics/onboardingMixpanel";

interface VerificationPageProps {
  onNext: () => void;
  onBack: () => void;
  canGoBack?: boolean;
}

export default function VerificationPage({
  onNext,
  onBack,
  canGoBack = true,
}: VerificationPageProps) {
  const reduxUser = useSelector(
    (state: any) => state.user?.currentUser || state.user?.user
  );

  const [sessionUrl, setSessionUrl] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<
    "loading" | "pending" | "in_progress" | "approved" | "declined" | "expired" | "error"
  >("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return (
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth <= 768
    );
  });

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const isStagingBypass = isStagingCreatorOnboardingBypassEnabled();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ) || window.innerWidth <= 768
      );
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getUserId = useCallback((): string => {
    if (reduxUser?._id) return String(reduxUser._id);
    if (reduxUser?.id) return String(reduxUser.id);
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?._id) return String(parsed._id);
        if (parsed?.id) return String(parsed.id);
      }
    } catch { }
    return localStorage.getItem("userId") || "";
  }, [reduxUser]);

  const checkStatus = useCallback(
    async (quiet = false): Promise<boolean> => {
      const userId = getUserId();
      if (!userId) return false;

      try {
        const res = await getDiditVerificationByUserIdAPI(userId);
        const latest = res?.didit_verifications?.[0];
        const rawStatus =
          latest?.status ||
          res?.status ||
          res?.data?.status ||
          res?.verification?.status;

        if (!rawStatus) return false;

        const normalized = normalizeDiditStatus(rawStatus);

        if (isDiditSuccessStatus(normalized)) {
          if (isMountedRef.current) {
            setVerificationStatus("approved");
            setErrorMessage(null);
          }
          showToast("Identity verification approved!", "success");
          return true;
        } else if (isDiditFailureStatus(normalized)) {
          if (isMountedRef.current) {
            setVerificationStatus(normalized as any);
          }
          trackDiditVerificationFailureOutcome({
            user_id: userId,
            status: normalized,
            failure_source: "status_poll",
            attempt_number: getDiditVerificationAttemptCount(),
          });
          return false;
        }
      } catch (err) {
        if (!quiet) {
          console.warn("Error checking Didit verification status:", err);
        }
      }
      return false;
    },
    [getUserId]
  );

  const initSession = useCallback(
    async (isRetry = false) => {
      const userId = getUserId();
      if (!userId) {
        setErrorMessage("User session not found. Please log in again.");
        setVerificationStatus("error");
        return;
      }

      setVerificationStatus("loading");
      setErrorMessage(null);

      try {
        registerDiditVerificationAttempt({
          user_id: userId,
          source: isRetry ? "retry_after_failure" : "user_click",
        });

        // Callback redirects back to Step 7 (monetization) after mobile verification completes
        const callbackUrl = `${window.location.origin}/apply-as-creator?step=7`;
        const workflowId =
          process.env.REACT_APP_DIDIT_WORKFLOW_ID || "atieA3fSSDWhrunPFEAkOQ";
        const result = await createDiditSessionAPI(userId, callbackUrl, workflowId);

        const url = result?.session_url || result?.url || result?.verification_url;

        if (url && isMountedRef.current) {
          setSessionUrl(url);
          setVerificationStatus("pending");
        } else {
          throw new Error("No session URL returned from DidIt service");
        }
      } catch (err: any) {
        console.error("Error creating Didit session:", err);

        const workflowId =
          process.env.REACT_APP_DIDIT_WORKFLOW_ID || "atieA3fSSDWhrunPFEAkOQ";

        if (err?.type === "ACTIVE_VERIFICATION_EXISTS") {
          const existingStatus = normalizeDiditStatus(err?.status);
          const existingUrl = err?.verification_url || err?.session_url;

          if (isDiditSuccessStatus(existingStatus)) {
            if (isMountedRef.current) {
              setVerificationStatus("approved");
            }
            showToast("Your verification is already approved!", "success");
            return;
          }

          if (isDiditFailureStatus(existingStatus) && err?.verification_id) {
            try {
              await deleteVerificationAPI(err.verification_id);
              const retryRes = await createDiditSessionAPI(
                userId,
                `${window.location.origin}/apply-as-creator?step=7`,
                workflowId
              );
              const retryUrl =
                retryRes?.session_url || retryRes?.url || retryRes?.verification_url;
              if (retryUrl && isMountedRef.current) {
                setSessionUrl(retryUrl);
                setVerificationStatus("pending");
                return;
              }
            } catch (delErr) {
              console.warn("Failed to recreate Didit session:", delErr);
            }
          }

          if (existingUrl && isMountedRef.current) {
            setSessionUrl(existingUrl);
            setVerificationStatus("pending");
            return;
          }
        }

        // If backend session creation fails, fallback to direct UniLink so user is not blocked
        const fallbackUrl =
          process.env.REACT_APP_DIDIT_WORKFLOW_URL ||
          "https://verify.sugarcribs.com/u/atieA3fSSDWhrunPFEAkOQ";
        if (fallbackUrl && isMountedRef.current) {
          console.info("Using Didit UniLink workflow as fallback session URL:", fallbackUrl);
          setSessionUrl(fallbackUrl);
          setVerificationStatus("pending");
          return;
        }

        const msg =
          err?.message ||
          (typeof err === "string" ? err : "Failed to initialize verification session");
        if (isMountedRef.current) {
          setErrorMessage(msg);
          setVerificationStatus("error");
        }
        trackDiditVerificationFailureOutcome({
          user_id: userId,
          status: "session_create_error",
          failure_source: "session_create",
          failure_reason: msg,
          attempt_number: getDiditVerificationAttemptCount(),
        });
      }
    },
    [getUserId]
  );

  useEffect(() => {
    isMountedRef.current = true;

    (async () => {
      const alreadyApproved = await checkStatus(true);
      if (!alreadyApproved && isMountedRef.current) {
        initSession();
      }
    })();

    return () => {
      isMountedRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [checkStatus, initSession]);

  useEffect(() => {
    if (verificationStatus !== "pending" && verificationStatus !== "in_progress") {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }

    pollTimerRef.current = setInterval(async () => {
      const approved = await checkStatus(true);
      if (approved && pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }, 3000);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [verificationStatus, checkStatus]);

  const handleContinuePress = async () => {
    if (isStagingBypass) {
      onNext();
      return;
    }

    if (verificationStatus === "approved") {
      onNext();
      return;
    }

    // ON MOBILE: The user is on their phone, so launching the verification session directly
    if (isMobile && sessionUrl) {
      window.location.href = sessionUrl;
      return;
    }

    // ON DESKTOP: Check if mobile verification just finished
    const approved = await checkStatus(false);
    if (approved) {
      onNext();
    } else {
      showToast(
        "Please complete the verification on your phone before continuing.",
        "info"
      );
    }
  };

  const isApproved = verificationStatus === "approved";
  const isLoading = verificationStatus === "loading";
  const isFailed =
    verificationStatus === "declined" ||
    verificationStatus === "expired" ||
    verificationStatus === "error";

  return (
    <div
      className={parentStyles.formCard}
      style={{ textAlign: "center", padding: "40px 20px" }}
    >
      <h1 className={`${parentStyles.formTitle} ${parentStyles.didItTitle}`}>
        Complete Identity Verification
      </h1>

      <p className={`${parentStyles.formSubtitle} ${parentStyles.didItSubTitle}`}>
        {isMobile
          ? "Verify your ID document and take a selfie using your phone's camera"
          : "Scan the QR code to upload your ID for verification"}
      </p>

      {/* Staging mode banner only when staging bypass key is active */}
      {isStagingBypass && (
        <div
          style={{
            backgroundColor: "rgba(254, 90, 151, 0.12)",
            border: "1px dashed #FE5A97",
            borderRadius: 8,
            padding: "8px 16px",
            marginBottom: 20,
            display: "inline-block",
            fontSize: 13,
            color: "#FFFFFF",
          }}
        >
          ⚡ <strong>Staging Mode Active:</strong> You can test verification or click Continue to bypass.
        </div>
      )}

      {/* Verification Card: QR code on desktop, Mobile prompt on phone */}
      <div style={{ marginBottom: "30px" }}>
        <div className={parentStyles.qrCard}>
          {isLoading ? (
            <div className={parentStyles.qrPlaceholder}>
              <RefreshCw className={parentStyles.spin} size={32} color="#FE5A97" />
              <span style={{ fontSize: "13px", color: "#666" }}>
                Preparing verification session...
              </span>
            </div>
          ) : isApproved ? (
            <div className={parentStyles.qrSuccess}>
              <CheckCircle size={56} color="#10B981" />
              <span style={{ fontSize: "16px", fontWeight: 600, color: "#10B981" }}>
                Identity Verified!
              </span>
            </div>
          ) : isFailed || errorMessage ? (
            <div className={parentStyles.qrPlaceholder}>
              <AlertCircle size={40} color="#EF4444" />
              <span style={{ fontSize: "13px", color: "#D32F2F" }}>
                {errorMessage || `Status: ${verificationStatus}. Verification incomplete.`}
              </span>
              <button
                type="button"
                onClick={() => initSession(true)}
                style={{
                  marginTop: 6,
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "1px solid #FE5A97",
                  backgroundColor: "#fff",
                  color: "#FE5A97",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                Try Again
              </button>
            </div>
          ) : isMobile ? (
            /* Clean mobile card when user is on a phone */
            <div
              className={parentStyles.qrPlaceholder}
              style={{
                width: "220px",
                height: "220px",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                cursor: sessionUrl ? "pointer" : "default",
              }}
              onClick={() => {
                if (sessionUrl) window.location.href = sessionUrl;
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(254, 90, 151, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={32} color="#FE5A97" />
              </div>
              <span
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#161616",
                }}
              >
                Mobile ID Check
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "#666",
                  lineHeight: "1.4",
                }}
              >
                Tap Continue below to capture your ID and take a selfie.
              </span>
            </div>
          ) : sessionUrl ? (
            /* Clean QR Code on Desktop */
            <QRCodeCanvas
              value={sessionUrl}
              size={220}
              bgColor="#ffffff"
              fgColor="#000000"
              level="H"
              imageSettings={{
                src: Logo,
                height: 32,
                width: 52,
                excavate: true,
              }}
            />
          ) : (
            <div className={parentStyles.qrPlaceholder}>
              <span style={{ fontSize: "13px", color: "#666" }}>
                No session URL available
              </span>
              <button
                type="button"
                onClick={() => initSession(true)}
                style={{
                  marginTop: 6,
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: "1px solid #FE5A97",
                  backgroundColor: "#fff",
                  color: "#FE5A97",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>

      <p
        className={parentStyles.formSubtitle}
        style={{
          fontSize: "15px",
          marginBottom: "30px",
          color: "#E0E0E0",
          maxWidth: "400px",
          margin: "0 auto 40px",
          lineHeight: "1.5",
        }}
      >
        Complete the verification process to continue and start your creator journey.
      </p>

      <div className={parentStyles.buttonsDidIt}>
        <button
          type="button"
          className={`${parentStyles.didItBackBtn} ${!canGoBack ? parentStyles.didItBackBtnDisabled : ""
            }`}
          onClick={() => {
            if (!canGoBack) {
              showToast("Back not allowed", "info");
              return;
            }
            onBack();
          }}
          title={!canGoBack ? "Back not allowed" : undefined}
        >
          <ArrowLeft size={20} /> Back
        </button>

        <button
          type="button"
          className={parentStyles.didItContinueBtn}
          onClick={handleContinuePress}
          disabled={isLoading}
          style={isLoading ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
        >
          {isApproved
            ? "Continue To Next Step"
            : isStagingBypass
              ? "Continue (Staging Bypass)"
              : "Continue To Verify Your Account"}
        </button>
      </div>
    </div>
  );
}
